/**
 * User profile helpers — piggybacks Firestore `users/{uid}` with likes,
 * shareable calendars, and friends.
 */

import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { countExplorePickups } from '../explore/exploreDrops';
import { exploredSarajevoPercent, fogRevealCellsFromProfile } from '../explore/fogReveal';
import { interestsFromProfile, normalizeUserInterests } from '../shared/contentRelevance';
import { getEventCalendarDay, startOfLocalDay } from '../shared/dateUtils';
import { getPublishedEventById } from '../shared/firestore';
import { getEventDisplayTitle } from '../instagram/instagramEventDisplay';
import { createErrorLog } from '../shared/errorLogs';
import { BIO_MIN_LENGTH, EARN_COINS, awardMapHas, getEarnCoins, isAwardRecorded } from './earnCatalog';
import { levelFromProfile } from './userLevels';
export const USERS_COLLECTION = 'users';
export const SHARED_CALENDARS_COLLECTION = 'sharedCalendars';
export const PUBLIC_PROFILES_COLLECTION = 'publicProfiles';
export const USER_EMAIL_INDEX_COLLECTION = 'userEmailIndex';
export const USER_USERNAME_INDEX_COLLECTION = 'userUsernameIndex';
export const USER_INSTAGRAM_INDEX_COLLECTION = 'userInstagramIndex';
export const USER_DISPLAY_NAME_INDEX_COLLECTION = 'userDisplayNameIndex';
export const FRIEND_REQUESTS_COLLECTION = 'friendRequests';
/** Firestore collection name — wire value unchanged (see userNotifications.js). */
const USER_NOTIFICATIONS_COLLECTION = 'userNotifications';
const NOTIF_TYPE_FRIEND_ACCEPTED = 'friend_accepted';

export function friendAcceptedNotifId(requestId) {
  return `friend_accepted_${requestId}`;
}

export const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const PROFILE_PHOTO_TYPES = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const USER_ROLES = Object.freeze(['user', 'partner', 'gameMaker', 'admin', 'superAdmin']);

export const SUPER_ADMIN_EMAILS = Object.freeze(['kapo.faris@gmail.com']);

export function roleLabel(role) {
  if (role === 'superAdmin') return 'super-admin';
  return role || 'user';
}

export function isAdminRole(role) {
  return role === 'admin' || role === 'superAdmin';
}

export function isSuperAdminEmail(email) {
  return SUPER_ADMIN_EMAILS.includes(normalizeEmailKey(email));
}

export function isSuperAdmin({ role, email } = {}) {
  return role === 'superAdmin' || isSuperAdminEmail(email);
}

export function homePathForRole(role) {
  switch (role) {
    case 'admin':
    case 'superAdmin':
      return '/adminhub';
    case 'partner':
      return '/partner';
    case 'gameMaker':
      return '/game-maker';
    case 'user':
      return '/profile';
    default:
      return '/';
  }
}

/** Shipped amounts. Live payouts use getEarnCoins() at grant time. */
export const M_COIN_REWARDS = Object.freeze({
  FIRST_EVENT_LIKE: EARN_COINS.EVENT_SAVE,
  FIRST_LOCATION_LIKE: EARN_COINS.PLACE_SAVE,
  FIRST_USERNAME: EARN_COINS.USERNAME,
  FRIEND: EARN_COINS.FRIEND,
  FIRST_PHOTO: EARN_COINS.PHOTO,
  FIRST_BIO: EARN_COINS.BIO,
  FIRST_MESSAGE: EARN_COINS.FIRST_MESSAGE,
});

function hasFriendAward(awards, otherUid) {
  const id = String(otherUid || '').trim();
  if (!id) return true;
  return awardMapHas(awards?.friends, id);
}

/** Add coin increment + award keys for peers not already in `mCoinAwards.friends`. */
function applyFriendAwards(patch, awards, otherUids) {
  let earned = 0;
  const payout = getEarnCoins().FRIEND;
  (otherUids || []).forEach((otherUid) => {
    const id = String(otherUid || '').trim();
    if (!id || hasFriendAward(awards, id)) return;
    patch[`mCoinAwards.friends.${id}`] = payout;
    earned += payout;
  });
  if (earned > 0) patch.mCoins = increment(earned);
  return earned;
}

export const SHOP_ITEMS = Object.freeze([
  {
    id: 'dark-mode',
    title: 'Dark Mode',
    price: 200,
    blurb: 'Unlock dark theme on your Profile (toggle under Appearance).',
    rank: 'unique',
    category: 'theme',
    glyph: '◐',
  },
  {
    id: 'fog-trail-ember',
    title: 'Ember Fog Trail',
    price: 75,
    blurb: 'Warm ember particles along your reveal path on the Game map.',
    rank: 'common',
    category: 'map',
    glyph: '✧',
  },
  {
    id: 'pin-skin-compass',
    title: 'Compass Pin',
    price: 50,
    blurb: 'Swap your walker marker for a classic compass rose.',
    rank: 'common',
    category: 'map',
    glyph: '◎',
  },
  {
    id: 'badge-early-walker',
    title: 'Early Walker Badge',
    price: 40,
    blurb: 'Profile flair for those who hit the streets first.',
    rank: 'common',
    category: 'badge',
    glyph: '★',
  },
  {
    id: 'frame-paper-edge',
    title: 'Paper Edge Frame',
    price: 90,
    blurb: 'Torn-paper border around your collected drop cards.',
    rank: 'common',
    category: 'frame',
    glyph: '▤',
  },
  {
    id: 'boost-pickup-surge',
    title: 'Pickup Surge',
    price: 120,
    blurb: 'Potential short boost to pickup coin rewards (coming soon).',
    rank: 'unique',
    category: 'boost',
    glyph: '▲',
  },
  {
    id: 'theme-midnight-ink',
    title: 'Midnight Ink',
    price: 180,
    blurb: 'Deep ink accents for profile headers and cards.',
    rank: 'unique',
    category: 'theme',
    glyph: '◈',
  },
  {
    id: 'pin-skin-lantern',
    title: 'Lantern Pin',
    price: 65,
    blurb: 'A soft lantern glow for your location marker.',
    rank: 'common',
    category: 'map',
    glyph: '☽',
  },
  {
    id: 'badge-cartographer',
    title: 'Cartographer Badge',
    price: 110,
    blurb: 'Show you map the city one drop at a time.',
    rank: 'unique',
    category: 'badge',
    glyph: '⌖',
  },
  {
    id: 'fog-trail-aurora',
    title: 'Aurora Fog Trail',
    price: 220,
    blurb: 'Cool aurora wash following your fog reveal.',
    rank: 'epic',
    category: 'map',
    glyph: '≋',
  },
  {
    id: 'frame-gold-foil',
    title: 'Gold Foil Frame',
    price: 250,
    blurb: 'Shimmering foil edge for legendary-feeling collections.',
    rank: 'epic',
    category: 'frame',
    glyph: '▣',
  },
  {
    id: 'invite-perk-double',
    title: 'Double Invite Perk',
    price: 150,
    blurb: 'Potential bonus coins when friends join from your invite.',
    rank: 'unique',
    category: 'boost',
    glyph: '＋',
  },
  {
    id: 'pin-skin-kite',
    title: 'Kite Pin',
    price: 55,
    blurb: 'A playful kite marker that catches the wind.',
    rank: 'common',
    category: 'map',
    glyph: '△',
  },
  {
    id: 'badge-night-owl',
    title: 'Night Owl Badge',
    price: 85,
    blurb: 'For late walks and after-dark drops.',
    rank: 'common',
    category: 'badge',
    glyph: '☾',
  },
  {
    id: 'theme-sunset-wash',
    title: 'Sunset Wash',
    price: 160,
    blurb: 'Warm sunset gradient accents across profile chrome.',
    rank: 'unique',
    category: 'theme',
    glyph: '◉',
  },
  {
    id: 'boost-create-spark',
    title: 'Create Spark',
    price: 140,
    blurb: 'Potential bonus when you place a new drop.',
    rank: 'unique',
    category: 'boost',
    glyph: '✦',
  },
  {
    id: 'frame-stencil-ink',
    title: 'Stencil Ink Frame',
    price: 95,
    blurb: 'Bold stencil outline around drop thumbnails.',
    rank: 'common',
    category: 'frame',
    glyph: '▦',
  },
  {
    id: 'fog-trail-spark',
    title: 'Spark Fog Trail',
    price: 100,
    blurb: 'Tiny sparks kick up as you clear fog.',
    rank: 'unique',
    category: 'map',
    glyph: '·',
  },
  {
    id: 'badge-collector',
    title: 'Collector Badge',
    price: 130,
    blurb: 'Flex a growing shelf of picked-up drops.',
    rank: 'unique',
    category: 'badge',
    glyph: '◆',
  },
  {
    id: 'pin-skin-anchor',
    title: 'Anchor Pin',
    price: 70,
    blurb: 'Steady anchor marker for coastal wanderers.',
    rank: 'common',
    category: 'map',
    glyph: '⚓',
  },
  {
    id: 'theme-forest-canopy',
    title: 'Forest Canopy',
    price: 170,
    blurb: 'Leafy green accents for a quieter profile vibe.',
    rank: 'unique',
    category: 'theme',
    glyph: '❀',
  },
  {
    id: 'frame-neon-pulse',
    title: 'Neon Pulse Frame',
    price: 280,
    blurb: 'Electric pulse border for epic collection cards.',
    rank: 'epic',
    category: 'frame',
    glyph: '▥',
  },
  {
    id: 'boost-weekly-cache',
    title: 'Weekly Cache',
    price: 300,
    blurb: 'Potential weekly coin cache claim (coming soon).',
    rank: 'epic',
    category: 'boost',
    glyph: '▣',
  },
  {
    id: 'badge-legend-path',
    title: 'Legend Path Badge',
    price: 400,
    blurb: 'Rare flair for pathfinders who go the distance.',
    rank: 'legendary',
    category: 'badge',
    glyph: '♛',
  },
  {
    id: 'fog-trail-mythic',
    title: 'Mythic Fog Trail',
    price: 500,
    blurb: 'Legendary reveal trail with mythic shimmer.',
    rank: 'legendary',
    category: 'map',
    glyph: '✶',
  },
  {
    id: 'winamp-player',
    title: 'MapToBe Radio',
    price: 100,
    blurb: 'Unlock the floating MapToBe Radio player on every page.',
    rank: 'unique',
    category: 'boost',
    glyph: '♫',
  },
]);

export const SHOP_ITEM_DARK_MODE = 'dark-mode';
export const SHOP_ITEM_WINAMP = 'winamp-player';
/** SKUs listed in Shop — other SHOP_ITEMS stay purchasable if already owned. */
export const SHOP_LIVE_ITEM_IDS = Object.freeze([
  SHOP_ITEM_DARK_MODE,
  SHOP_ITEM_WINAMP,
]);
export const THEME_STORAGE_KEY = 'm2b-theme';

/** First-path segments reserved so /{username} cannot collide with app routes. */
export const RESERVED_USERNAMES = Object.freeze(
  new Set([
    'login',
    'signup',
    'profile',
    'friends',
    'shop',
    'levels',
    'events',
    'desavanja',
    'sarajevoevents',
    'desavanjasarajevo',
    'places',
    'c',
    'u',
    'me',
    'partner',
    'game-maker',
    'admin',
    'adminhub',
    'game',
    'explore',
    'explore-bosna',
    'explorebosna',
    'loyalty',
    'sova2',
    'sova3',
    'sova3-admin',
    'sova3admin',
    'fabrika2',
    'memory',
    'sidequests',
    'blog',
    'vibecheck',
    'upload-vibecheck',
    'uploadvibecheck',
    'api',
    'assets',
    'static',
    'index',
    'home',
    'www',
    'artists',
    'halloween',
    'giveaway-demo',
    'giveawaydemo',
    'giveaway',
  ])
);

export function isReservedUsername(username) {
  const key = normalizeUsername(username);
  return Boolean(key && RESERVED_USERNAMES.has(key));
}

export function profileInviteUrl(username, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  const key = normalizeUsername(username);
  if (!key || !origin) return '';
  return `${origin}/${encodeURIComponent(key)}`;
}

export function normalizeEmailKey(email) {
  return String(email || '').trim().toLowerCase();
}

/** MapToBe username: lowercase a-z, 0-9, . _ ; strips leading @. */
export function normalizeUsername(value) {
  let s = String(value || '').trim().toLowerCase();
  if (s.startsWith('@')) s = s.slice(1);
  return s.replace(/[^a-z0-9._]/g, '');
}

/** Instagram handle without @ or profile URL path. */
export function normalizeInstagramHandle(value) {
  let s = String(value || '').trim();
  s = s.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '');
  s = s.split(/[/?#]/)[0] || '';
  if (s.startsWith('@')) s = s.slice(1);
  return s.toLowerCase().replace(/[^a-z0-9._]/g, '');
}

export function normalizeDisplayNameKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export function looksLikeInstagramQuery(value) {
  const s = String(value || '').trim();
  if (/instagram\.com/i.test(s)) return true;
  return s.startsWith('@') && !looksLikeEmail(s);
}

export function instagramProfileUrl(handle) {
  const key = normalizeInstagramHandle(handle);
  return key ? `https://instagram.com/${key}` : '';
}

function assertUsernameFormat(username) {
  if (!username) return;
  if (username.length < 3 || username.length > 30) {
    throw new Error('Username must be 3–30 characters.');
  }
  if (!/^[a-z0-9]([a-z0-9._]*[a-z0-9])?$/.test(username)) {
    throw new Error('Username can use letters, numbers, . and _.');
  }
  if (RESERVED_USERNAMES.has(username)) {
    throw new Error('That username is reserved. Try another.');
  }
}

export function likedMapSize(map) {
  if (!map || typeof map !== 'object') return 0;
  return Object.keys(map).length;
}

/**
 * Firestore map keys used in updateDoc field paths cannot contain ~ * / . [ ].
 * Document ids may still include those characters; store likes under a safe key.
 * @param {unknown} id
 * @returns {string}
 */
export function likedMapFieldKey(id) {
  return String(id || '').replace(/[~*/.[\]]/g, '_');
}

export function likedEventsList(profile) {
  const map = profile?.likedEvents;
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map)
    .map(([key, meta]) => {
      const row = meta && typeof meta === 'object' ? meta : {};
      return { ...row, id: row.eventId || key };
    })
    .sort((a, b) => {
      const aMs = toMillis(a.eventStartAt);
      const bMs = toMillis(b.eventStartAt);
      if (aMs !== bMs) return aMs - bMs;
      return String(a.dateStr || '').localeCompare(String(b.dateStr || ''));
    });
}

export function likedLocationsList(profile) {
  const map = profile?.likedLocations;
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map)
    .map(([id, meta]) => ({ id, ...meta }))
    .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
}

export function isEventLiked(profile, eventId) {
  if (!eventId || !profile?.likedEvents) return false;
  const map = profile.likedEvents;
  if (map[eventId]) return true;
  const safeKey = likedMapFieldKey(eventId);
  return safeKey !== eventId && Boolean(map[safeKey]);
}

export function isLocationLiked(profile, locationId) {
  return Boolean(profile?.likedLocations && profile.likedLocations[locationId]);
}

function toMillis(value) {
  if (!value) return Number.MAX_SAFE_INTEGER;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Timestamp) return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

function denormEventEntry(event) {
  const eventId = event?.id ? String(event.id) : '';
  return {
    likedAt: serverTimestamp(),
    ...(eventId ? { eventId } : {}),
    title: getEventDisplayTitle(event) || event.title || '',
    dateStr: event.dateStr || '',
    eventStartAt: event.eventStartAt || null,
    location: event.location || '',
    description: event.description || '',
    imageUrl: event.imageUrl || event.imagePath || '',
    editorialPick: event.editorialPick || null,
    instagramUrl: event.instagramUrl || '',
    posterLink: event.posterLink || '',
    categories: event.categories || event.category || null,
  };
}

function denormLocationEntry(place) {
  return {
    likedAt: serverTimestamp(),
    title: place.title || place.name || '',
  };
}

export async function createUserProfile(uid, { email, displayName = '', role = 'user' } = {}) {
  const emailKey = normalizeEmailKey(email);
  const initialRole = role === 'partner' ? 'partner' : 'user';
  const payload = {
    role: initialRole,
    email: String(email || '').trim(),
    displayName: String(displayName || '').trim(),
    username: '',
    instagramHandle: '',
    bio: '',
    photoURL: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    disabled: false,
    installNoticePending: true,
    likedEvents: {},
    likedLocations: {},
    interests: [],
    calendarShareToken: null,
    friendIds: [],
    mCoins: 0,
    mCoinAwards: {
      username: false,
      photo: false,
      bio: false,
      likedEvents: {},
      likedLocations: {},
      friends: {},
      firstMessages: {},
    },
    shopOwned: {},
    fogReveal: {},
    explorePickups: {},
    explorePoints: 0,
    explorePointAwards: { pickups: {}, creates: {} },
  };
  await setDoc(doc(db, USERS_COLLECTION, uid), payload);
  if (emailKey) {
    await setDoc(doc(db, USER_EMAIL_INDEX_COLLECTION, emailKey), {
      uid,
      displayName: payload.displayName,
      email: payload.email,
      username: '',
      photoURL: null,
    });
  }
  // Always-on public calendar projection for friends + /c/:token links
  await ensureCalendarShare(uid);
  const created = await getUserProfile(uid);
  await syncPublicProfile(uid, created);
  return created;
}

/**
 * Create users/{uid} only if missing (Google / first social login).
 * Never overwrites an existing role or profile.
 * @param {import('firebase/auth').User} user
 * @param {{ role?: 'user' | 'partner' }} [opts]
 */
export async function ensureUserProfile(user, opts = {}) {
  if (!user?.uid || user.isAnonymous) return null;
  const existing = await getUserProfile(user.uid);
  if (existing) return existing;
  await createUserProfile(user.uid, {
    email: user.email || '',
    displayName: user.displayName || '',
    role: opts.role === 'partner' ? 'partner' : 'user',
  });
  return getUserProfile(user.uid);
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, USERS_COLLECTION, uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

function formatPublicExploredPercent(pct) {
  if (!Number.isFinite(pct) || pct <= 0) return '0%';
  if (pct < 0.1) return '<0.1%';
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

/** Safe public projection for guest invite profiles (no email / likes maps). */
export function buildPublicProfilePayload(profile) {
  if (!profile) return null;
  const cells = fogRevealCellsFromProfile(profile);
  return {
    displayName: profile.displayName || '',
    username: profile.username || '',
    photoURL: profile.photoURL || null,
    bio: profile.bio || '',
    instagramHandle: profile.instagramHandle || '',
    calendarShareToken: profile.calendarShareToken || null,
    coins: Math.max(0, Math.floor(Number(profile.mCoins) || 0)),
    explored: formatPublicExploredPercent(exploredSarajevoPercent(cells)),
    dropsCollected: countExplorePickups(profile),
    dropsMade: likedMapSize(profile?.explorePointAwards?.creates),
    eventsLiked: likedMapSize(profile?.likedEvents),
    placesLiked: likedMapSize(profile?.likedLocations),
  };
}

export async function syncPublicProfile(uid, profileInput = null) {
  if (!uid) return null;
  const profile = profileInput || (await getUserProfile(uid));
  if (!profile) return null;
  const payload = buildPublicProfilePayload(profile);
  await setDoc(
    doc(db, PUBLIC_PROFILES_COLLECTION, uid),
    { ...payload, updatedAt: serverTimestamp() },
    { merge: true }
  );
  return payload;
}

export async function getPublicProfile(uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, PUBLIC_PROFILES_COLLECTION, uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

async function claimUniqueIndex(collectionName, key, uid, extra = {}) {
  if (!key) return;
  const ref = doc(db, collectionName, key);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data()?.uid && snap.data().uid !== uid) {
    const label =
      collectionName === USER_USERNAME_INDEX_COLLECTION
        ? 'Username'
        : 'Instagram handle';
    throw new Error(`${label} is already taken.`);
  }
  await setDoc(ref, { uid, ...extra });
}

async function releaseUniqueIndex(collectionName, key, uid) {
  if (!key) return;
  const ref = doc(db, collectionName, key);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data()?.uid === uid) {
    await deleteDoc(ref);
  }
}

async function syncDisplayNameIndex(uid, prevName, nextName) {
  const prevKey = normalizeDisplayNameKey(prevName);
  const nextKey = normalizeDisplayNameKey(nextName);
  if (prevKey === nextKey) return;

  if (prevKey) {
    const prevRef = doc(db, USER_DISPLAY_NAME_INDEX_COLLECTION, prevKey);
    const prevSnap = await getDoc(prevRef);
    if (prevSnap.exists()) {
      const uids = (prevSnap.data()?.uids || []).filter((id) => id !== uid);
      if (uids.length === 0) await deleteDoc(prevRef);
      else await setDoc(prevRef, { uids, displayName: prevSnap.data()?.displayName || '' });
    }
  }

  if (nextKey) {
    const nextRef = doc(db, USER_DISPLAY_NAME_INDEX_COLLECTION, nextKey);
    const nextSnap = await getDoc(nextRef);
    const uids = new Set(nextSnap.exists() ? nextSnap.data()?.uids || [] : []);
    uids.add(uid);
    await setDoc(nextRef, {
      uids: [...uids],
      displayName: String(nextName || '').trim(),
    });
  }
}

/**
 * Owner profile edit: displayName, username, Instagram, bio, photoURL, interests.
 * Rewrites lookup indexes and shared calendar labels when needed.
 */
export async function updateUserProfile(uid, fields = {}) {
  if (!uid) throw new Error('Missing user id.');
  const existing = await getUserProfile(uid);
  if (!existing) throw new Error('User profile not found.');

  const displayName =
    fields.displayName !== undefined
      ? String(fields.displayName || '').trim()
      : existing.displayName || '';
  const username =
    fields.username !== undefined
      ? normalizeUsername(fields.username)
      : normalizeUsername(existing.username || '');
  const instagramHandle =
    fields.instagramHandle !== undefined
      ? normalizeInstagramHandle(fields.instagramHandle)
      : normalizeInstagramHandle(existing.instagramHandle || '');
  const bio =
    fields.bio !== undefined
      ? String(fields.bio || '').trim().slice(0, 280)
      : existing.bio || '';
  const photoURL =
    fields.photoURL !== undefined ? fields.photoURL || null : existing.photoURL || null;
  const interests =
    fields.interests !== undefined
      ? normalizeUserInterests(fields.interests)
      : Array.isArray(existing.interests)
        ? normalizeUserInterests(existing.interests)
        : normalizeUserInterests(existing.personalities);

  assertUsernameFormat(username);
  if (instagramHandle && (instagramHandle.length < 1 || instagramHandle.length > 30)) {
    throw new Error('Instagram handle looks invalid.');
  }

  const prevUsername = normalizeUsername(existing.username || '');
  const prevIg = normalizeInstagramHandle(existing.instagramHandle || '');
  const prevDisplay = existing.displayName || '';

  if (username && username !== prevUsername) {
    const taken = await getDoc(doc(db, USER_USERNAME_INDEX_COLLECTION, username));
    if (taken.exists() && taken.data()?.uid && taken.data().uid !== uid) {
      throw new Error('Username is already taken.');
    }
  }
  if (instagramHandle && instagramHandle !== prevIg) {
    const taken = await getDoc(doc(db, USER_INSTAGRAM_INDEX_COLLECTION, instagramHandle));
    if (taken.exists() && taken.data()?.uid && taken.data().uid !== uid) {
      throw new Error('Instagram handle is already taken.');
    }
  }

  const awards = existing.mCoinAwards || {};
  const prevPhoto = String(existing.photoURL || '').trim();
  const nextPhoto = String(photoURL || '').trim();
  const prevBio = String(existing.bio || '').trim();
  const prevBioQualified = prevBio.length >= BIO_MIN_LENGTH;
  const nextBioQualified = bio.length >= BIO_MIN_LENGTH;

  let earned = 0;
  const patch = {
    displayName,
    username: username || '',
    instagramHandle: instagramHandle || '',
    bio,
    photoURL,
    interests,
    updatedAt: serverTimestamp(),
  };
  if (username && !prevUsername && !isAwardRecorded(awards.username)) {
    const payout = getEarnCoins().USERNAME;
    patch['mCoinAwards.username'] = payout;
    earned += payout;
  }
  if (nextPhoto && !prevPhoto && !isAwardRecorded(awards.photo)) {
    const payout = getEarnCoins().PHOTO;
    patch['mCoinAwards.photo'] = payout;
    earned += payout;
  }
  if (nextBioQualified && !prevBioQualified && !isAwardRecorded(awards.bio)) {
    const payout = getEarnCoins().BIO;
    patch['mCoinAwards.bio'] = payout;
    earned += payout;
  }
  if (earned > 0) patch.mCoins = increment(earned);

  await updateDoc(doc(db, USERS_COLLECTION, uid), patch);

  if (prevUsername && prevUsername !== username) {
    await releaseUniqueIndex(USER_USERNAME_INDEX_COLLECTION, prevUsername, uid);
  }
  if (prevIg && prevIg !== instagramHandle) {
    await releaseUniqueIndex(USER_INSTAGRAM_INDEX_COLLECTION, prevIg, uid);
  }
  if (username) {
    await claimUniqueIndex(USER_USERNAME_INDEX_COLLECTION, username, uid, {
      username,
      displayName,
    });
  }
  if (instagramHandle) {
    await claimUniqueIndex(USER_INSTAGRAM_INDEX_COLLECTION, instagramHandle, uid, {
      instagramHandle,
      displayName,
    });
  }

  await syncDisplayNameIndex(uid, prevDisplay, displayName);

  const emailKey = normalizeEmailKey(existing.email || '');
  if (emailKey) {
    await setDoc(doc(db, USER_EMAIL_INDEX_COLLECTION, emailKey), {
      uid,
      displayName,
      email: existing.email || '',
      username: username || '',
      photoURL,
    });
  }

  if (existing.calendarShareToken) {
    await refreshSharedCalendarProjection(uid);
  }

  const profile = await getUserProfile(uid);
  await syncPublicProfile(uid, profile);
  return { profile, earned };
}

/** Deduct mCoins and mark shop item owned (no feature unlock yet). */
export async function purchaseShopItem(uid, itemId) {
  if (!uid || !itemId) throw new Error('Missing uid or item.');
  const item = SHOP_ITEMS.find((i) => i.id === itemId);
  if (!item) throw new Error('Unknown shop item.');
  const profile = await getUserProfile(uid);
  if (!profile) throw new Error('User profile not found.');
  if (profile.shopOwned?.[itemId]) throw new Error('Already owned.');
  const balance = Number(profile.mCoins) || 0;
  if (balance < item.price) throw new Error('Not enough m coins.');
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    mCoins: increment(-item.price),
    [`shopOwned.${itemId}`]: true,
    updatedAt: serverTimestamp(),
  });
  return getUserProfile(uid);
}

/** Ensure current user's indexes exist (legacy accounts opening Profile). */
export async function ensureProfileIndexes(uid) {
  const profile = await getUserProfile(uid);
  if (!profile) return null;
  const emailKey = normalizeEmailKey(profile.email || '');
  if (emailKey) {
    await setDoc(doc(db, USER_EMAIL_INDEX_COLLECTION, emailKey), {
      uid,
      displayName: profile.displayName || '',
      email: profile.email || '',
      username: profile.username || '',
      photoURL: profile.photoURL || null,
    });
  }
  const username = normalizeUsername(profile.username || '');
  if (username) {
    await claimUniqueIndex(USER_USERNAME_INDEX_COLLECTION, username, uid, {
      username,
      displayName: profile.displayName || '',
    });
  }
  const ig = normalizeInstagramHandle(profile.instagramHandle || '');
  if (ig) {
    await claimUniqueIndex(USER_INSTAGRAM_INDEX_COLLECTION, ig, uid, {
      instagramHandle: ig,
      displayName: profile.displayName || '',
    });
  }
  const nameKey = normalizeDisplayNameKey(profile.displayName || '');
  if (nameKey) {
    await syncDisplayNameIndex(uid, '', profile.displayName || '');
  }
  await syncPublicProfile(uid, profile);
  return profile;
}

export async function uploadProfilePhoto(uid, file) {
  if (!uid) throw new Error('Missing user id.');
  if (!file) throw new Error('Choose a photo to upload.');
  if (!PROFILE_PHOTO_TYPES.includes(file.type)) {
    throw new Error('Use a JPG, PNG, or WebP photo.');
  }
  if (file.size > PROFILE_PHOTO_MAX_BYTES) {
    throw new Error('Photo must be under 2 MB.');
  }
  const ext =
    file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  const storageRef = ref(storage, `profile-photos/${uid}/avatar.${ext}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
}

export async function listUsers() {
  const snapshot = await getDocs(collection(db, USERS_COLLECTION));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listPartnerUsers() {
  const snapshot = await getDocs(
    query(collection(db, USERS_COLLECTION), where('role', '==', 'partner'))
  );
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function updateUserRole(uid, role) {
  if (!USER_ROLES.includes(role)) {
    throw new Error(`Invalid role: ${role}`);
  }
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    role,
    updatedAt: serverTimestamp(),
  });
}

export async function setUserDisabled(uid, disabled) {
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    disabled: Boolean(disabled),
    updatedAt: serverTimestamp(),
  });
}

export function isUserDisabled(profile) {
  return Boolean(profile?.disabled);
}

export async function setEventLiked(uid, event, liked) {
  const eventId = event?.id;
  if (!uid || !eventId) throw new Error('Missing uid or event id');
  const mapKey = likedMapFieldKey(eventId);
  const ref = doc(db, USERS_COLLECTION, uid);
  let earned = 0;
  if (liked) {
    const profile = await getUserProfile(uid);
    const awards = profile?.mCoinAwards?.likedEvents;
    const alreadyAwarded = isAwardRecorded(awards?.[mapKey]) || isAwardRecorded(awards?.[eventId]);
    const patch = {
      [`likedEvents.${mapKey}`]: denormEventEntry(event),
      updatedAt: serverTimestamp(),
    };
    if (!alreadyAwarded) {
      const payout = getEarnCoins().EVENT_SAVE;
      patch[`mCoinAwards.likedEvents.${mapKey}`] = payout;
      if (payout > 0) {
        earned = payout;
        patch.mCoins = increment(earned);
      }
    }
    await updateDoc(ref, patch);
  } else {
    await updateDoc(ref, {
      [`likedEvents.${mapKey}`]: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
  await ensureCalendarShare(uid);
  return { earned };
}

export async function setLocationLiked(uid, place, liked) {
  const locationId = place?.id;
  if (!uid || !locationId) throw new Error('Missing uid or location id');
  const ref = doc(db, USERS_COLLECTION, uid);
  let earned = 0;
  if (liked) {
    const profile = await getUserProfile(uid);
    const alreadyAwarded = isAwardRecorded(profile?.mCoinAwards?.likedLocations?.[locationId]);
    const patch = {
      [`likedLocations.${locationId}`]: denormLocationEntry(place),
      updatedAt: serverTimestamp(),
    };
    if (!alreadyAwarded) {
      const payout = getEarnCoins().PLACE_SAVE;
      patch[`mCoinAwards.likedLocations.${locationId}`] = payout;
      if (payout > 0) {
        earned = payout;
        patch.mCoins = increment(earned);
      }
    }
    await updateDoc(ref, patch);
  } else {
    await updateDoc(ref, {
      [`likedLocations.${locationId}`]: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
  return { earned };
}

/**
 * Merge newly revealed fog-of-war cells onto users/{uid}.fogReveal.{cellKey}.
 * Cell keys look like "row:col" (see fogReveal.js).
 */
export async function mergeFogRevealCells(uid, cellKeys = []) {
  if (!uid) throw new Error('Missing user id.');
  const keys = Array.from(
    new Set(
      (Array.isArray(cellKeys) ? cellKeys : []).filter(
        (key) => typeof key === 'string' && /^-?\d+:-?\d+$/.test(key)
      )
    )
  );
  if (!keys.length) return 0;

  const patch = { updatedAt: serverTimestamp() };
  keys.forEach((key) => {
    patch[`fogReveal.${key}`] = true;
  });
  await updateDoc(doc(db, USERS_COLLECTION, uid), patch);
  return keys.length;
}

/** Admin/QA: clear walk-reveal progress for one user. */
export async function resetUserFogReveal(uid) {
  if (!uid) throw new Error('Missing user id.');
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    fogReveal: {},
    updatedAt: serverTimestamp(),
  });
}

function randomShareToken() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function buildSharedCalendarEvents(profile) {
  return likedEventsList(profile).map((entry) => ({
    id: entry.id,
    title: entry.title || '',
    dateStr: entry.dateStr || '',
    eventStartAt: entry.eventStartAt || null,
    location: entry.location || '',
    description: entry.description || '',
    imageUrl: entry.imageUrl || '',
    editorialPick: entry.editorialPick || null,
    instagramUrl: entry.instagramUrl || '',
    posterLink: entry.posterLink || '',
    categories: entry.categories || null,
  }));
}

/** Prefer live published event docs; fall back to liked/share denorm fields. */
export async function hydrateEventEntries(entries, { requirePublished = false } = {}) {
  const hydrated = await Promise.all(
    (entries || []).map(async (entry) => {
      if (!entry?.id) return null;
      try {
        const full = await getPublishedEventById(entry.id);
        if (full) return full;
      } catch {
        if (requirePublished) return null;
      }
      if (requirePublished) return null;
      return {
        id: entry.id,
        title: entry.title || '',
        dateStr: entry.dateStr || '',
        eventStartAt: entry.eventStartAt || null,
        location: entry.location || '',
        description: entry.description || '',
        imageUrl: entry.imageUrl || '',
        editorialPick: entry.editorialPick || null,
        instagramUrl: entry.instagramUrl || '',
        posterLink: entry.posterLink || '',
        categories: entry.categories || null,
      };
    })
  );
  return hydrated.filter(Boolean);
}

export async function refreshSharedCalendarProjection(uid) {
  const profile = await getUserProfile(uid);
  if (!profile?.calendarShareToken) return null;
  const token = profile.calendarShareToken;
  await setDoc(doc(db, SHARED_CALENDARS_COLLECTION, token), {
    ownerUid: uid,
    displayName: profile.displayName || profile.email || 'MapToBe user',
    photoURL: profile.photoURL || null,
    username: profile.username || '',
    events: buildSharedCalendarEvents(profile),
    updatedAt: serverTimestamp(),
  });
  await syncPublicProfile(uid, profile);
  return token;
}

/** Ensure a public /c/:token projection exists and is up to date (always-on share). */
export async function ensureCalendarShare(uid) {
  const profile = await getUserProfile(uid);
  if (!profile) throw new Error('User profile not found');
  if (profile.calendarShareToken) {
    return refreshSharedCalendarProjection(uid);
  }
  return enableCalendarShare(uid);
}

export async function enableCalendarShare(uid) {
  const profile = await getUserProfile(uid);
  if (!profile) throw new Error('User profile not found');
  const token = profile.calendarShareToken || randomShareToken();
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    calendarShareToken: token,
    updatedAt: serverTimestamp(),
  });
  await setDoc(doc(db, SHARED_CALENDARS_COLLECTION, token), {
    ownerUid: uid,
    displayName: profile.displayName || profile.email || 'MapToBe user',
    photoURL: profile.photoURL || null,
    username: profile.username || '',
    events: buildSharedCalendarEvents({ ...profile, calendarShareToken: token }),
    updatedAt: serverTimestamp(),
  });
  await syncPublicProfile(uid, { ...profile, calendarShareToken: token });
  return token;
}

export async function disableCalendarShare(uid) {
  const profile = await getUserProfile(uid);
  const token = profile?.calendarShareToken;
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    calendarShareToken: null,
    updatedAt: serverTimestamp(),
  });
  if (token) {
    await deleteDoc(doc(db, SHARED_CALENDARS_COLLECTION, token));
  }
}

export async function getSharedCalendar(token) {
  if (!token) return null;
  const snap = await getDoc(doc(db, SHARED_CALENDARS_COLLECTION, token));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function lookupUserByEmail(email) {
  const key = normalizeEmailKey(email);
  if (!key) return null;
  const snap = await getDoc(doc(db, USER_EMAIL_INDEX_COLLECTION, key));
  if (!snap.exists()) return null;
  return snap.data();
}

export async function lookupUserByUsername(username) {
  const key = normalizeUsername(username);
  if (!key) return null;
  const snap = await getDoc(doc(db, USER_USERNAME_INDEX_COLLECTION, key));
  if (!snap.exists()) return null;
  return snap.data();
}

export async function lookupUserByInstagram(handle) {
  const key = normalizeInstagramHandle(handle);
  if (!key) return null;
  const snap = await getDoc(doc(db, USER_INSTAGRAM_INDEX_COLLECTION, key));
  if (!snap.exists()) return null;
  return snap.data();
}

export async function lookupUsersByDisplayName(name) {
  const key = normalizeDisplayNameKey(name);
  if (!key) return [];
  const snap = await getDoc(doc(db, USER_DISPLAY_NAME_INDEX_COLLECTION, key));
  if (!snap.exists()) return [];
  return snap.data()?.uids || [];
}

const FRIEND_SEARCH_PREFIX_END = '\uf8ff';
const FRIEND_SEARCH_MAX = 8;

function looksLikeEmailPrefix(value) {
  const s = String(value || '').trim();
  if (!s.includes('@') || s.startsWith('@')) return false;
  return !looksLikeEmail(s);
}

async function prefixIndexDocs(collectionName, prefix, max = FRIEND_SEARCH_MAX) {
  if (!prefix) return [];
  const q = query(
    collection(db, collectionName),
    orderBy(documentId()),
    startAt(prefix),
    endAt(`${prefix}${FRIEND_SEARCH_PREFIX_END}`),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Live friend suggestions via prefix queries on lookup indexes.
 * Returns hydrated candidates; empty/short queries return [].
 */
export async function searchFriendCandidates(
  rawQuery,
  { currentUid = null, limit: maxResults = FRIEND_SEARCH_MAX } = {}
) {
  const queryText = String(rawQuery || '').trim();
  if (queryText.length < 2) return [];

  const byUid = new Map();
  const addHit = (uid, via, partial = {}) => {
    if (!uid || (currentUid && uid === currentUid) || byUid.has(uid)) return;
    byUid.set(uid, { uid, via, ...partial });
  };

  const usernamePrefix = normalizeUsername(queryText);
  const displayPrefix = normalizeDisplayNameKey(queryText);
  const tasks = [];

  if (usernamePrefix.length >= 2) {
    tasks.push(
      prefixIndexDocs(USER_USERNAME_INDEX_COLLECTION, usernamePrefix).then((docs) => {
        docs.forEach((row) => {
          if (row.uid) {
            addHit(row.uid, 'username', {
              username: row.username || row.id,
              displayName: row.displayName || '',
            });
          }
        });
      })
    );
  }

  if (displayPrefix.length >= 2) {
    tasks.push(
      prefixIndexDocs(USER_DISPLAY_NAME_INDEX_COLLECTION, displayPrefix).then((docs) => {
        docs.forEach((row) => {
          (row.uids || []).forEach((uid) => {
            addHit(uid, 'displayName', { displayName: row.displayName || row.id });
          });
        });
      })
    );
  }

  if (looksLikeInstagramQuery(queryText)) {
    const igPrefix = normalizeInstagramHandle(queryText);
    if (igPrefix.length >= 2) {
      tasks.push(
        prefixIndexDocs(USER_INSTAGRAM_INDEX_COLLECTION, igPrefix).then((docs) => {
          docs.forEach((row) => {
            if (row.uid) {
              addHit(row.uid, 'instagram', {
                instagramHandle: row.instagramHandle || row.id,
                displayName: row.displayName || '',
              });
            }
          });
        })
      );
    }
  }

  if (looksLikeEmail(queryText)) {
    tasks.push(
      lookupUserByEmail(queryText).then((hit) => {
        if (hit?.uid) {
          addHit(hit.uid, 'email', {
            email: hit.email || queryText,
            displayName: hit.displayName || '',
            username: hit.username || '',
            photoURL: hit.photoURL || null,
          });
        }
      })
    );
  } else if (looksLikeEmailPrefix(queryText)) {
    const emailPrefix = normalizeEmailKey(queryText);
    if (emailPrefix.length >= 2) {
      tasks.push(
        prefixIndexDocs(USER_EMAIL_INDEX_COLLECTION, emailPrefix).then((docs) => {
          docs.forEach((row) => {
            if (row.uid) {
              addHit(row.uid, 'email', {
                email: row.email || row.id,
                displayName: row.displayName || '',
                username: row.username || '',
                photoURL: row.photoURL || null,
              });
            }
          });
        })
      );
    }
  }

  await Promise.all(tasks);

  const uids = [...byUid.keys()].slice(0, maxResults);
  const profiles = await Promise.all(uids.map((uid) => getUserProfile(uid)));

  return uids
    .map((uid, i) => {
      const hit = byUid.get(uid);
      const profile = profiles[i];
      if (!profile) {
        return {
          uid,
          via: hit.via,
          displayName: hit.displayName || '',
          username: hit.username || '',
          photoURL: hit.photoURL || null,
          instagramHandle: hit.instagramHandle || '',
        };
      }
      return {
        uid,
        via: hit.via,
        displayName: profile.displayName || hit.displayName || '',
        username: profile.username || hit.username || '',
        photoURL: profile.photoURL || hit.photoURL || null,
        instagramHandle: profile.instagramHandle || hit.instagramHandle || '',
      };
    })
    .filter(Boolean);
}

/**
 * Resolve a friend query to a target uid.
 * Order: email → Instagram (@ or URL) → username → exact display name.
 */
export async function resolveFriendLookup(rawQuery) {
  const queryText = String(rawQuery || '').trim();
  if (!queryText) throw new Error('Enter an email, @username, Instagram, or name.');

  if (looksLikeEmail(queryText)) {
    const hit = await lookupUserByEmail(queryText);
    if (!hit?.uid) throw new Error('No user found with that email.');
    return { uid: hit.uid, via: 'email' };
  }

  if (looksLikeInstagramQuery(queryText)) {
    const igHit = await lookupUserByInstagram(queryText);
    if (igHit?.uid) return { uid: igHit.uid, via: 'instagram' };
    const userHit = await lookupUserByUsername(queryText);
    if (userHit?.uid) return { uid: userHit.uid, via: 'username' };
    throw new Error('No user found with that Instagram or @username.');
  }

  const usernameHit = await lookupUserByUsername(queryText);
  if (usernameHit?.uid) return { uid: usernameHit.uid, via: 'username' };

  const igHit = await lookupUserByInstagram(queryText);
  if (igHit?.uid) return { uid: igHit.uid, via: 'instagram' };

  const nameUids = await lookupUsersByDisplayName(queryText);
  if (nameUids.length === 1) return { uid: nameUids[0], via: 'displayName' };
  if (nameUids.length > 1) {
    throw new Error('Several people share that name. Try their @username instead.');
  }

  throw new Error('No user found. Try email, @username, Instagram, or exact name.');
}

export function friendRequestId(fromUid, toUid) {
  return `${fromUid}_${toUid}`;
}

/** Send a friend request by target uid (e.g. shared calendar owner). */
export async function sendFriendRequestToUid(fromUid, toUid) {
  if (!fromUid || !toUid) throw new Error('Missing user id.');
  if (toUid === fromUid) throw new Error('You cannot add yourself.');
  const [fromProfile, toProfile] = await Promise.all([
    getUserProfile(fromUid),
    getUserProfile(toUid),
  ]);
  if (!toProfile) throw new Error('User not found.');
  if ((fromProfile?.friendIds || []).includes(toUid)) {
    throw new Error('Already friends.');
  }
  const id = friendRequestId(fromUid, toUid);
  const existing = await getDoc(doc(db, FRIEND_REQUESTS_COLLECTION, id));
  if (existing.exists() && existing.data().status === 'pending') {
    throw new Error('Friend request already sent.');
  }
  const toEmail = normalizeEmailKey(toProfile.email || '');
  await setDoc(doc(db, FRIEND_REQUESTS_COLLECTION, id), {
    fromUid,
    toUid,
    toEmail,
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return {
    id,
    toUid,
    displayName: toProfile.displayName || toProfile.email || '',
  };
}

export async function sendFriendRequest(fromUid, queryText) {
  const target = await resolveFriendLookup(queryText);
  return sendFriendRequestToUid(fromUid, target.uid);
}

export async function listFriendRequestsForUser(uid) {
  // Single-field queries avoid composite indexes; filter status client-side.
  const incomingQ = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('toUid', '==', uid)
  );
  const outgoingQ = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('fromUid', '==', uid)
  );
  const [incomingSnap, outgoingSnap] = await Promise.all([
    getDocs(incomingQ),
    getDocs(outgoingQ),
  ]);
  const pending = (d) => d.data().status === 'pending';
  return {
    incoming: incomingSnap.docs.filter(pending).map((d) => ({ id: d.id, ...d.data() })),
    outgoing: outgoingSnap.docs.filter(pending).map((d) => ({ id: d.id, ...d.data() })),
  };
}

/** Enrich pending incoming rows with display label / photo / username. */
export async function labelIncomingFriendRequests(incoming) {
  const rows = incoming || [];
  const profiles = await Promise.all(rows.map((req) => getUserProfile(req.fromUid)));
  return rows.map((req, i) => {
    const p = profiles[i];
    return {
      ...req,
      fromLabel:
        p?.displayName ||
        (p?.username ? `@${p.username}` : '') ||
        p?.email ||
        req.fromUid,
      fromPhotoURL: p?.photoURL || null,
      fromUsername: p?.username || null,
    };
  });
}

/**
 * Live pending incoming friend requests for `uid` (labeled).
 * @returns {() => void} unsubscribe
 */
export function subscribeIncomingFriendRequests(uid, onChange, onError) {
  if (!uid) {
    onChange([]);
    return () => {};
  }
  const incomingQ = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('toUid', '==', uid)
  );
  return onSnapshot(
    incomingQ,
    async (snap) => {
      try {
        const pending = snap.docs
          .filter((d) => d.data().status === 'pending')
          .map((d) => ({ id: d.id, ...d.data() }));
        const labeled = await labelIncomingFriendRequests(pending);
        onChange(labeled);
      } catch (err) {
        if (onError) onError(err);
        else console.error('label incoming friend requests failed:', err);
      }
    },
    (err) => {
      if (onError) onError(err);
      else console.error('subscribeIncomingFriendRequests failed:', err);
    }
  );
}

export async function respondToFriendRequest(requestId, accept, currentUid) {
  const ref = doc(db, FRIEND_REQUESTS_COLLECTION, requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Request not found.');
  const data = snap.data();
  if (data.toUid !== currentUid) throw new Error('Not your request to answer.');
  if (data.status !== 'pending') throw new Error('Request is no longer pending.');

  if (!accept) {
    await updateDoc(ref, { status: 'declined', updatedAt: serverTimestamp() });
    return { earned: 0 };
  }

  await updateDoc(ref, { status: 'accepted', updatedAt: serverTimestamp() });

  // Owner-only writes: recipient adds sender; sender syncs via syncAcceptedFriends.
  const toRef = doc(db, USERS_COLLECTION, data.toUid);
  const toSnap = await getDoc(toRef);
  const toData = toSnap.data() || {};
  const toIds = new Set(toData.friendIds || []);
  const alreadyFriend = toIds.has(data.fromUid);
  toIds.add(data.fromUid);
  const friendPatch = { friendIds: [...toIds], updatedAt: serverTimestamp() };
  const earned = alreadyFriend ? 0 : applyFriendAwards(friendPatch, toData.mCoinAwards, [data.fromUid]);
  await updateDoc(toRef, friendPatch);

  try {
    await setDoc(
      doc(db, USER_NOTIFICATIONS_COLLECTION, friendAcceptedNotifId(requestId)),
      {
        type: NOTIF_TYPE_FRIEND_ACCEPTED,
        fromUid: currentUid,
        toUid: data.fromUid,
        status: 'unread',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error('friend_accepted notif write failed:', err);
    try {
      await createErrorLog({
        message: err.message || 'Could not notify sender of accepted friend request.',
        pageLabel: 'Friends',
        source: 'friend-accepted-notif',
        kind: 'intent',
        uid: currentUid,
        pathname: typeof window !== 'undefined' ? window.location.pathname : '/friends',
      });
    } catch {
      /* logging must never fail the accept */
    }
  }

  return { earned };
}

/** Sender pulls accepted requests into their own friendIds (owner write only). */
export async function syncAcceptedFriends(uid) {
  const outgoingQ = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('fromUid', '==', uid)
  );
  const snap = await getDocs(outgoingQ);
  if (snap.empty) return { earned: 0 };
  const profile = await getUserProfile(uid);
  const ids = new Set(profile?.friendIds || []);
  const added = [];
  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.status !== 'accepted') return;
    const toUid = data.toUid;
    if (toUid && !ids.has(toUid)) {
      ids.add(toUid);
      added.push(toUid);
    }
  });
  if (added.length === 0) return { earned: 0 };
  const friendPatch = { friendIds: [...ids], updatedAt: serverTimestamp() };
  const earned = applyFriendAwards(friendPatch, profile?.mCoinAwards, added);
  await updateDoc(doc(db, USERS_COLLECTION, uid), friendPatch);
  return { earned };
}

export async function cancelFriendRequest(requestId, currentUid) {
  if (!requestId || !currentUid) throw new Error('Missing request.');
  const ref = doc(db, FRIEND_REQUESTS_COLLECTION, requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Request not found.');
  const data = snap.data();
  if (data.fromUid !== currentUid) throw new Error('Not your request to cancel.');
  if (data.status !== 'pending') throw new Error('Request is no longer pending.');
  await updateDoc(ref, { status: 'declined', updatedAt: serverTimestamp() });
}

function peerUidFromRequest(uid, data) {
  if (!data) return null;
  if (data.fromUid === uid) return data.toUid || null;
  if (data.toUid === uid) return data.fromUid || null;
  return null;
}

/** Pull `removed` requests out of own friendIds (owner write only). */
export async function syncRemovedFriends(uid) {
  if (!uid) return;
  const incomingQ = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('toUid', '==', uid)
  );
  const outgoingQ = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('fromUid', '==', uid)
  );
  const [incomingSnap, outgoingSnap] = await Promise.all([
    getDocs(incomingQ),
    getDocs(outgoingQ),
  ]);
  const removeUids = new Set();
  [...incomingSnap.docs, ...outgoingSnap.docs].forEach((d) => {
    const data = d.data();
    if (data.status !== 'removed') return;
    const peer = peerUidFromRequest(uid, data);
    if (peer && peer !== uid) removeUids.add(peer);
  });
  if (removeUids.size === 0) return;
  const profile = await getUserProfile(uid);
  const current = profile?.friendIds || [];
  const next = current.filter((id) => !removeUids.has(id));
  if (next.length === current.length) return;
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    friendIds: next,
    updatedAt: serverTimestamp(),
  });
}

export async function unfriendUser(currentUid, otherUid) {
  if (!currentUid || !otherUid) throw new Error('Missing user id.');
  if (currentUid === otherUid) throw new Error('You cannot remove yourself.');
  const profile = await getUserProfile(currentUid);
  if (!profile) throw new Error('User profile not found.');
  const friendIds = profile.friendIds || [];
  if (!friendIds.includes(otherUid)) throw new Error('Not friends.');

  const ownRef = doc(db, FRIEND_REQUESTS_COLLECTION, friendRequestId(currentUid, otherUid));
  const theirRef = doc(db, FRIEND_REQUESTS_COLLECTION, friendRequestId(otherUid, currentUid));
  const [ownSnap, theirSnap] = await Promise.all([getDoc(ownRef), getDoc(theirRef)]);
  const stamp = { status: 'removed', updatedAt: serverTimestamp() };

  if (ownSnap.exists()) await updateDoc(ownRef, stamp);
  if (theirSnap.exists()) await updateDoc(theirRef, stamp);
  if (!ownSnap.exists() && !theirSnap.exists()) {
    const other = await getUserProfile(otherUid);
    await setDoc(ownRef, {
      fromUid: currentUid,
      toUid: otherUid,
      toEmail: normalizeEmailKey(other?.email || ''),
      status: 'removed',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  await updateDoc(doc(db, USERS_COLLECTION, currentUid), {
    friendIds: friendIds.filter((id) => id !== otherUid),
    updatedAt: serverTimestamp(),
  });
}

export async function getFriendsProfiles(friendIds = []) {
  const ids = [...new Set(friendIds)].filter(Boolean);
  const profiles = await Promise.all(ids.map((id) => getUserProfile(id)));
  return profiles.filter(Boolean).map((p) => ({
    id: p.id,
    displayName: p.displayName || '',
    email: p.email || '',
    username: p.username || '',
    instagramHandle: p.instagramHandle || '',
    photoURL: p.photoURL || null,
    bio: p.bio || '',
    calendarShareToken: p.calendarShareToken || null,
    likedLocations: p.likedLocations || {},
  }));
}

export function friendDisplayName(profile, calendar = null) {
  return (
    profile?.displayName ||
    (profile?.username ? `@${profile.username}` : '') ||
    calendar?.displayName ||
    profile?.email ||
    'MapToBe user'
  );
}

/**
 * Combine friends' saved-event calendars into one event-interest map.
 * Profiles and calendars use matching array indexes; a null calendar is skipped.
 * Friends always see each other's saves (no opt-in token required).
 */
export function aggregateFriendsGoing(profiles = [], calendars = []) {
  const byEventId = {};

  profiles.forEach((profile, index) => {
    const calendar = calendars[index];
    if (!profile?.id || !calendar) return;

    const friend = {
      id: profile.id,
      displayName: friendDisplayName(profile, calendar),
      photoURL: profile.photoURL || calendar?.photoURL || null,
    };

    (calendar.events || []).forEach((event) => {
      if (!event?.id) return;
      if (!byEventId[event.id]) {
        byEventId[event.id] = { event, friendsGoing: [] };
      }
      if (!byEventId[event.id].friendsGoing.some((item) => item.id === friend.id)) {
        byEventId[event.id].friendsGoing.push(friend);
      }
    });
  });

  return byEventId;
}

export function formatFriendsGoingLabel(friendsGoing = []) {
  if (friendsGoing.length === 0) return '';
  const firstName = friendsGoing[0].displayName || 'A friend';
  if (friendsGoing.length === 1) return `${firstName} wants to go`;
  const others = friendsGoing.length - 1;
  return `${firstName} + ${others} friend${others === 1 ? '' : 's'} want to go`;
}

/**
 * Friends' saved events from each friend's likedEvents (always-on among friends).
 * Optional hydrate loads full published event docs for the Friends page feed.
 */
export async function getFriendsGoingData(friendIds = [], { hydrate = true } = {}) {
  const ids = [...new Set(friendIds)].filter(Boolean);
  const fullProfiles = (await Promise.all(ids.map((id) => getUserProfile(id)))).filter(
    Boolean
  );
  const profiles = fullProfiles.map((p) => ({
    id: p.id,
    displayName: p.displayName || '',
    email: p.email || '',
    username: p.username || '',
    instagramHandle: p.instagramHandle || '',
    photoURL: p.photoURL || null,
    bio: p.bio || '',
    calendarShareToken: p.calendarShareToken || null,
  }));
  const calendars = fullProfiles.map((profile) => ({
    ownerUid: profile.id,
    displayName: profile.displayName || profile.email || 'MapToBe user',
    photoURL: profile.photoURL || null,
    username: profile.username || '',
    events: buildSharedCalendarEvents(profile),
  }));
  const aggregated = aggregateFriendsGoing(profiles, calendars);
  const displayProfiles = profiles.map((profile, index) => ({
    ...profile,
    displayName: friendDisplayName(profile, calendars[index]),
  }));
  const interestByEventId = Object.fromEntries(
    Object.entries(aggregated).map(([eventId, item]) => [
      eventId,
      item.friendsGoing,
    ])
  );

  if (!hydrate) {
    const events = Object.values(aggregated).map((item) => ({
      ...item.event,
      friendsGoing: item.friendsGoing,
    }));
    return { profiles: displayProfiles, events, interestByEventId };
  }

  const hydrated = await hydrateEventEntries(
    Object.values(aggregated).map((item) => item.event)
  );
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const events = hydrated
    .filter((event) => toMillis(event.eventStartAt) >= startOfToday.getTime())
    .sort((a, b) => {
      const timeDiff = toMillis(a.eventStartAt) - toMillis(b.eventStartAt);
      if (timeDiff !== 0) return timeDiff;
      return String(a.dateStr || '').localeCompare(String(b.dateStr || ''));
    })
    .map((event) => ({
      ...event,
      friendsGoing: interestByEventId[event.id] || [],
    }));

  return { profiles: displayProfiles, events, interestByEventId };
}

export function suggestedFriendActivityCoins(profile) {
  return Math.max(0, Math.floor(Number(profile?.mCoins) || 0));
}

/** @deprecated Prefer compareSuggestedFriendRows; kept for older call sites. */
export function scoreSuggestedFriend({
  mutualFriendCount = 0,
  sharedEventCount = 0,
  sharedPlaceCount = 0,
} = {}) {
  return (
    Math.max(0, Math.floor(Number(mutualFriendCount) || 0)) * 10
    + Math.max(0, Math.floor(Number(sharedEventCount) || 0))
    + Math.max(0, Math.floor(Number(sharedPlaceCount) || 0))
  );
}

export function countSharedMapKeys(a, b) {
  const mapA = a && typeof a === 'object' ? a : null;
  const mapB = b && typeof b === 'object' ? b : null;
  if (!mapA || !mapB) return 0;
  let n = 0;
  Object.keys(mapA).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(mapB, key)) n += 1;
  });
  return n;
}

/** Declared profile interests in common (interests, else legacy personalities). */
export function countSharedInterests(a, b) {
  const left = interestsFromProfile(a);
  const right = new Set(interestsFromProfile(b));
  if (!left.length || !right.size) return 0;
  return left.filter((label) => right.has(label)).length;
}

function suggestedFriendOverlap(row) {
  return (
    Math.max(0, Math.floor(Number(row?.sharedEventCount) || 0))
    + Math.max(0, Math.floor(Number(row?.sharedInterestCount) || 0))
  );
}

export function formatSuggestedFriendReason(
  mutualFriends = [],
  sharedEventCount = 0,
  sharedInterestCount = 0
) {
  const mutual = (mutualFriends || []).filter(Boolean);
  let reason = '';
  if (mutual.length === 1) {
    reason = `Friends with ${mutual[0].displayName || 'a friend'}`;
  } else if (mutual.length > 1) {
    const first = mutual[0].displayName || 'a friend';
    const others = mutual.length - 1;
    reason = `Friends with ${first} + ${others} other${others === 1 ? '' : 's'}`;
  }
  const shared = Math.max(0, Math.floor(Number(sharedEventCount) || 0));
  if (shared > 0) {
    const shareBit = `${shared} event${shared === 1 ? '' : 's'} in common`;
    reason = reason ? `${reason} · ${shareBit}` : shareBit;
  }
  const interests = Math.max(0, Math.floor(Number(sharedInterestCount) || 0));
  if (interests > 0) {
    const interestBit = `${interests} interest${interests === 1 ? '' : 's'} in common`;
    reason = reason ? `${reason} · ${interestBit}` : interestBit;
  }
  return reason;
}

export function formatFriendMatchReason({
  sharedEventCount = 0,
  sharedPlaceCount = 0,
} = {}) {
  const events = Math.max(0, Math.floor(Number(sharedEventCount) || 0));
  const places = Math.max(0, Math.floor(Number(sharedPlaceCount) || 0));
  const bits = [];
  if (events > 0) {
    bits.push(`${events} event${events === 1 ? '' : 's'} in common`);
  }
  if (places > 0) {
    bits.push(`${places} place${places === 1 ? '' : 's'} in common`);
  }
  return bits.join(' · ') || 'On MapToBe';
}

export function compareSuggestedFriendRows(a, b) {
  const mutualDiff =
    (b.mutualFriends?.length || 0) - (a.mutualFriends?.length || 0);
  if (mutualDiff !== 0) return mutualDiff;
  const overlapDiff = suggestedFriendOverlap(b) - suggestedFriendOverlap(a);
  if (overlapDiff !== 0) return overlapDiff;
  const levelDiff = (Number(b.level) || 0) - (Number(a.level) || 0);
  if (levelDiff !== 0) return levelDiff;
  return String(a.displayName || '').localeCompare(String(b.displayName || ''));
}

/**
 * Rank discoverable candidates: mutual friends, then shared events and interests,
 * then walker level, then name.
 * Pass a positive `limit` to cap results; omit/null returns the full ranked list.
 */
export function buildSuggestedFriends({
  uid,
  profile,
  friendProfiles = [],
  candidateProfiles = [],
  excludeUids = [],
  limit = null,
} = {}) {
  const exclude = new Set(
    [uid, ...(excludeUids || []), ...(friendProfiles || []).map((f) => f?.id)]
      .filter(Boolean)
  );
  const mutualByCandidate = new Map();

  (friendProfiles || []).forEach((friend) => {
    if (!friend?.id) return;
    const bridge = {
      id: friend.id,
      displayName: friendDisplayName(friend),
    };
    (friend.friendIds || []).forEach((fofId) => {
      if (!fofId || exclude.has(fofId)) return;
      if (!mutualByCandidate.has(fofId)) mutualByCandidate.set(fofId, []);
      const list = mutualByCandidate.get(fofId);
      if (!list.some((m) => m.id === bridge.id)) list.push(bridge);
    });
  });

  const rows = [];
  (candidateProfiles || []).forEach((candidate) => {
    if (!candidate?.id || exclude.has(candidate.id) || candidate.disabled) return;
    const mutualFriends = mutualByCandidate.get(candidate.id) || [];
    const sharedEventCount = countSharedMapKeys(
      profile?.likedEvents,
      candidate.likedEvents
    );
    const sharedPlaceCount = countSharedMapKeys(
      profile?.likedLocations,
      candidate.likedLocations
    );
    const sharedInterestCount = countSharedInterests(profile, candidate);
    const activityCoins = suggestedFriendActivityCoins(candidate);
    const level = levelFromProfile(candidate).level;
    rows.push({
      id: candidate.id,
      displayName: friendDisplayName(candidate),
      username: candidate.username || '',
      photoURL: candidate.photoURL || null,
      activityCoins,
      level,
      score: scoreSuggestedFriend({
        mutualFriendCount: mutualFriends.length,
        sharedEventCount,
        sharedPlaceCount,
      }),
      mutualFriends,
      sharedEventCount,
      sharedPlaceCount,
      sharedInterestCount,
      reason: formatSuggestedFriendReason(
        mutualFriends,
        sharedEventCount,
        sharedInterestCount
      ),
    });
  });

  const ranked = rows.sort(compareSuggestedFriendRows);
  if (limit == null || limit <= 0) return ranked;
  return ranked.slice(0, limit);
}

export function compareFriendMatchRows(a, b) {
  const scoreDiff = (Number(b?.score) || 0) - (Number(a?.score) || 0);
  if (scoreDiff !== 0) return scoreDiff;
  return String(a?.displayName || '').localeCompare(String(b?.displayName || ''));
}

/** Keep people with shared events/places, ranked by overlap score. Default cap 8. */
export function selectFriendMatches(rows = [], { limit = 8 } = {}) {
  const cap = limit == null || limit <= 0 ? 8 : Math.floor(Number(limit) || 8);
  return (rows || [])
    .filter(
      (row) =>
        Math.max(0, Math.floor(Number(row?.sharedEventCount) || 0)) +
          Math.max(0, Math.floor(Number(row?.sharedPlaceCount) || 0)) >
        0
    )
    .map((row) => ({
      ...row,
      reason: formatFriendMatchReason(row),
    }))
    .sort(compareFriendMatchRows)
    .slice(0, cap);
}

export function buildFriendMatches({
  uid,
  profile,
  friendProfiles = [],
  candidateProfiles = [],
  excludeUids = [],
  limit = 8,
} = {}) {
  const rows = buildSuggestedFriends({
    uid,
    profile,
    friendProfiles,
    candidateProfiles,
    excludeUids,
    limit: null,
  });
  return selectFriendMatches(rows, { limit });
}

async function listDiscoverableUidsFromIndexes() {
  const uids = new Set();
  const [usernameSnap, displaySnap] = await Promise.all([
    getDocs(collection(db, USER_USERNAME_INDEX_COLLECTION)),
    getDocs(collection(db, USER_DISPLAY_NAME_INDEX_COLLECTION)),
  ]);
  usernameSnap.docs.forEach((d) => {
    const id = d.data()?.uid;
    if (id) uids.add(id);
  });
  displaySnap.docs.forEach((d) => {
    const list = d.data()?.uids;
    if (!Array.isArray(list)) return;
    list.forEach((id) => {
      if (id) uids.add(id);
    });
  });
  return [...uids];
}

/**
 * Suggested friends: username + display-name index users, ranked mutual,
 * then shared events and interests, then walker level.
 * FoF mutual counts come from friend docs; full ranked list by default.
 */
export async function getSuggestedFriends({
  uid,
  profile,
  friendProfiles: providedFriends,
  friendIds,
  excludeUids = [],
  candidateCap = 500,
  limit = null,
} = {}) {
  if (!uid) return [];

  let friends = (providedFriends || []).filter((f) => f?.id);
  if (friends.length === 0) {
    const ids = [...new Set(friendIds || profile?.friendIds || [])].filter(Boolean);
    if (ids.length > 0) {
      friends = (await Promise.all(ids.map((id) => getUserProfile(id)))).filter(
        Boolean
      );
    }
  }

  const exclude = new Set(
    [uid, ...(excludeUids || []), ...friends.map((f) => f.id)].filter(Boolean)
  );

  const fofFreq = new Map();
  friends.forEach((friend) => {
    (friend.friendIds || []).forEach((fofId) => {
      if (!fofId || exclude.has(fofId)) return;
      fofFreq.set(fofId, (fofFreq.get(fofId) || 0) + 1);
    });
  });
  const fofIds = [...fofFreq.entries()]
    .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
    .map(([id]) => id);

  let discoverIds = [];
  try {
    discoverIds = (await listDiscoverableUidsFromIndexes()).filter(
      (id) => id && !exclude.has(id)
    );
  } catch (err) {
    console.error('Suggested friends index list failed:', err);
  }

  const orderedIds = [];
  const seen = new Set();
  fofIds.forEach((id) => {
    if (!seen.has(id)) {
      orderedIds.push(id);
      seen.add(id);
    }
  });
  discoverIds.forEach((id) => {
    if (!seen.has(id)) {
      orderedIds.push(id);
      seen.add(id);
    }
  });

  const toFetch = orderedIds.slice(0, Math.max(0, candidateCap));
  if (toFetch.length === 0) return [];

  const candidateProfiles = (
    await Promise.all(toFetch.map((id) => getUserProfile(id)))
  ).filter(Boolean);

  return buildSuggestedFriends({
    uid,
    profile,
    friendProfiles: friends,
    candidateProfiles,
    excludeUids,
    limit,
  });
}

/** True when the event’s calendar day (or eventStartAt fallback) is today or later. */
export function isUpcomingCalendarEvent(entry, now = new Date()) {
  if (!entry) return false;
  const startOfToday = startOfLocalDay(now).getTime();
  const day = getEventCalendarDay(entry);
  if (day) return day.getTime() >= startOfToday;
  const ms = toMillis(entry.eventStartAt);
  if (ms === Number.MAX_SAFE_INTEGER) return false;
  return ms >= startOfToday;
}

/** Upcoming events from a denorm / share list (no extra event fetches). */
export function upcomingCalendarEvents(entries, now = new Date()) {
  return (entries || []).filter((entry) => isUpcomingCalendarEvent(entry, now));
}

/** Upcoming liked events for profile calendars (denorm only; no extra event fetches). */
export function upcomingLikedEvents(profile, now = new Date()) {
  return upcomingCalendarEvents(likedEventsList(profile), now);
}
